(() => {
  if (window.__pdlLogicalProductCardsLoaded) return;
  window.__pdlLogicalProductCardsLoaded = true;

  const productCache = new Map();
  const cardCache = new WeakMap();
  let scheduled = 0;
  let workQueue = Promise.resolve();

  const normalize = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const safeNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  function productLink(card) {
    return card.querySelector('a.product-card__link[href*="/products/"], a[ref="productCardLink"][href*="/products/"], a[href*="/products/"]');
  }

  function previewThemeId() {
    return new URL(window.location.href).searchParams.get('preview_theme_id') || '';
  }

  async function fetchProduct(pathname) {
    if (productCache.has(pathname)) return productCache.get(pathname);

    const request = (async () => {
      const query = new URLSearchParams({ view: 'pdl-logical-data' });
      const previewId = previewThemeId();
      if (previewId) query.set('preview_theme_id', previewId);

      try {
        const response = await fetch(`${pathname}?${query.toString()}`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json, text/plain, */*' }
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const text = await response.text();
        const start = text.indexOf('{');
        const end = text.lastIndexOf('}');
        if (start < 0 || end <= start) throw new Error('Resposta técnica inválida');
        return JSON.parse(text.slice(start, end + 1));
      } catch (error) {
        const response = await fetch(`${pathname}.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw error;
        const product = await response.json();
        return {
          id: product.id,
          title: product.title,
          handle: product.handle,
          tags: product.tags || [],
          variants: (product.variants || []).map((variant) => ({
            id: variant.id,
            title: variant.title,
            sku: variant.sku || '',
            available: Boolean(variant.available),
            inventory_quantity: variant.available ? 1 : 0,
            price: safeNumber(variant.price),
            options: variant.options || [],
            locations: []
          }))
        };
      }
    })();

    productCache.set(pathname, request);
    return request;
  }

  function findVariant(product, url) {
    const requestedId = String(url.searchParams.get('variant') || '');
    const variants = Array.isArray(product.variants) ? product.variants : [];
    return variants.find((variant) => String(variant.id) === requestedId)
      || variants.find((variant) => variant.available)
      || variants[0]
      || null;
  }

  function locationFromMeta(card, product, variant) {
    const locations = Array.isArray(variant?.locations) ? variant.locations : [];
    const availableLocations = locations
      .filter((location) => location && location.available !== false)
      .map((location) => normalize(location.name));

    if (availableLocations.some((name) => name.includes('porto alegre'))) return 'poa';
    if (availableLocations.some((name) => name.includes('matriz') || name.includes('sao leopoldo'))) return 'matriz';

    const badge = card.querySelector('.location-badge');
    const badgeText = normalize(`${badge?.getAttribute('aria-label') || ''} ${badge?.textContent || ''}`);
    if (badgeText.includes('porto alegre')) return 'poa';
    if (badgeText.includes('matriz') || badgeText.includes('sao leopoldo')) return 'matriz';

    const tags = Array.isArray(product.tags) ? product.tags.map(normalize) : [];
    if (tags.some((tag) => tag.includes('porto alegre'))) return 'poa';
    if (tags.some((tag) => tag.includes('matriz') || tag.includes('sao leopoldo'))) return 'matriz';
    return 'unknown';
  }

  function colorFromProduct(product, variant) {
    const options = Array.isArray(product.options) ? product.options : [];
    const colorIndex = options.findIndex((option) => {
      const name = typeof option === 'string' ? option : option?.name;
      const normalized = normalize(name);
      return normalized.includes('cor') || normalized.includes('color');
    });
    if (colorIndex < 0) return '';
    return String(variant?.options?.[colorIndex] || '').trim();
  }

  async function readCard(card) {
    const link = productLink(card);
    if (!link) return null;

    const href = new URL(link.getAttribute('href') || link.href, window.location.origin);
    if (!href.pathname.includes('/products/')) return null;

    const cacheKey = `${href.pathname}|${href.searchParams.get('variant') || ''}`;
    const previous = cardCache.get(card);
    if (previous?.cacheKey === cacheKey) return previous.value;

    try {
      const product = await fetchProduct(href.pathname);
      const variant = findVariant(product, href);
      if (!variant) return null;

      const variantCard = Boolean(card.querySelector('product-card.pk-variant-product-card, .pk-variant-grid-card'));
      const displayedTitle = String(
        card.querySelector('.pk-variant-grid-info h3')?.textContent
        || card.querySelector('a[ref="productTitleLink"]')?.textContent
        || product.title
        || ''
      ).trim();
      const displayedColor = String(
        card.querySelector('.pk-variant-grid-info > p')?.textContent
        || colorFromProduct(product, variant)
        || ''
      ).trim();

      const value = {
        card,
        link,
        product,
        variant,
        productId: String(product.id || card.dataset.productId || ''),
        variantId: String(variant.id || ''),
        sku: String(variant.sku || '').trim(),
        skuSetKey: normalize(card.dataset.pdlProductSkus || ''),
        title: displayedTitle,
        titleKey: normalize(displayedTitle || product.title),
        color: displayedColor,
        colorKey: variantCard ? normalize(displayedColor) : '',
        price: safeNumber(variant.price),
        inventory: safeNumber(variant.inventory_quantity),
        available: Boolean(variant.available) || safeNumber(variant.inventory_quantity) > 0,
        location: locationFromMeta(card, product, variant),
        pathname: href.pathname,
        variantCard
      };

      cardCache.set(card, { cacheKey, value });
      return value;
    } catch (error) {
      console.warn('[Pedalokos] Leitura técnica indisponível; usando os dados visíveis do card:', href.pathname, error);

      const variantCard = Boolean(card.querySelector('product-card.pk-variant-product-card, .pk-variant-grid-card'));
      const displayedTitle = String(
        card.dataset.pdlProductTitle
        || card.querySelector('.pk-variant-grid-info h3')?.textContent
        || card.querySelector('a[ref="productTitleLink"]')?.textContent
        || ''
      ).trim();
      const displayedColor = String(card.querySelector('.pk-variant-grid-info > p')?.textContent || '').trim();
      const fallbackProductId = String(card.dataset.productId || href.pathname);

      const value = {
        card,
        link,
        product: {
          id: fallbackProductId,
          handle: href.pathname.split('/').filter(Boolean).pop() || '',
          tags: [],
          variants: []
        },
        variant: null,
        productId: fallbackProductId,
        variantId: String(href.searchParams.get('variant') || ''),
        sku: '',
        skuSetKey: normalize(card.dataset.pdlProductSkus || ''),
        title: displayedTitle,
        titleKey: normalize(displayedTitle),
        color: displayedColor,
        colorKey: variantCard ? normalize(displayedColor) : '',
        price: safeNumber(card.dataset.pdlProductPrice),
        inventory: 0,
        available: true,
        location: locationFromMeta(card, {}, {}),
        pathname: href.pathname,
        variantCard
      };

      cardCache.set(card, { cacheKey, value });
      return value;
    }
  }

  function samePrice(a, b) {
    return a.price === b.price || a.price === 0 || b.price === 0;
  }

  function shouldMerge(a, b) {
    if (!samePrice(a, b)) return false;

    const sameSku = a.sku && b.sku && normalize(a.sku) === normalize(b.sku);
    if (sameSku) return true;

    const sameSkuSet = a.skuSetKey && b.skuSetKey && a.skuSetKey === b.skuSetKey;
    if (sameSkuSet) {
      if (a.variantCard || b.variantCard) return a.colorKey === b.colorKey;
      return true;
    }

    if (!a.titleKey || a.titleKey !== b.titleKey) return false;
    if (a.variantCard || b.variantCard) return a.colorKey === b.colorKey;
    return true;
  }

  function buildGroups(items) {
    const parent = items.map((_, index) => index);
    const find = (index) => {
      while (parent[index] !== index) {
        parent[index] = parent[parent[index]];
        index = parent[index];
      }
      return index;
    };
    const union = (a, b) => {
      const rootA = find(a);
      const rootB = find(b);
      if (rootA !== rootB) parent[rootB] = rootA;
    };

    for (let a = 0; a < items.length; a += 1) {
      for (let b = a + 1; b < items.length; b += 1) {
        if (shouldMerge(items[a], items[b])) union(a, b);
      }
    }

    const groups = new Map();
    items.forEach((item, index) => {
      const root = find(index);
      if (!groups.has(root)) groups.set(root, []);
      groups.get(root).push(item);
    });
    return [...groups.values()];
  }

  function restoreCards(cards) {
    cards.forEach((card) => {
      if (card.dataset.pdlLogicalHidden === 'true') {
        card.hidden = false;
        card.removeAttribute('aria-hidden');
        delete card.dataset.pdlLogicalHidden;
      }

      card.classList.remove('pdl-logical-card--merged');
      card.removeAttribute('data-pdl-logical-sources');
      card.removeAttribute('data-pdl-logical-canonical');
      card.querySelectorAll('.pdl-logical-generated-badge').forEach((badge) => badge.remove());

      const nativeBadge = card.querySelector('.location-badge[data-pdl-original-label]');
      if (nativeBadge) {
        const label = nativeBadge.querySelector('.location-badge__label');
        if (label) label.textContent = nativeBadge.dataset.pdlOriginalLabel;
        nativeBadge.setAttribute('aria-label', nativeBadge.dataset.pdlOriginalAria || '');
        nativeBadge.classList.remove('location-badge--logical-merged');
      }
    });
  }

  function canonicalScore(item, index) {
    let score = 0;
    if (item.location === 'matriz') score += 100000;
    else if (item.location === 'poa') score += 10000;
    if (item.available) score += 1000;
    score += Math.min(item.inventory, 999);
    score -= index / 1000;
    return score;
  }

  function chooseCanonical(group) {
    return group.reduce((best, item, index) => {
      const score = canonicalScore(item, index);
      return !best || score > best.score ? { item, score } : best;
    }, null).item;
  }

  function chooseStableCanonical(group, previousCanonicalCards, preferredExistingCards) {
    const previousCanonical = group.find((item) => previousCanonicalCards.has(item.card));
    if (previousCanonical) return previousCanonical;

    const existingCandidates = group.filter((item) => preferredExistingCards.has(item.card));
    if (existingCandidates.length) return chooseCanonical(existingCandidates);

    return chooseCanonical(group);
  }

  function badgeMarkup() {
    return `<span class="pdl-logical-generated-badge" role="status" title="Disponível em São Leopoldo e Porto Alegre" aria-label="Disponível em São Leopoldo e Porto Alegre">
      <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M12 2a7 7 0 0 0-7 7c0 5.1 7 13 7 13s7-7.9 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5Z"/></svg>
      <span>2 lojas</span>
    </span>`;
  }

  function showMergedBadge(card) {
    const nativeBadge = card.querySelector('.location-badge');
    if (nativeBadge) {
      const label = nativeBadge.querySelector('.location-badge__label');
      if (!nativeBadge.dataset.pdlOriginalLabel) {
        nativeBadge.dataset.pdlOriginalLabel = String(label?.textContent || '').trim();
        nativeBadge.dataset.pdlOriginalAria = nativeBadge.getAttribute('aria-label') || '';
      }
      if (label) label.textContent = '2 lojas';
      nativeBadge.setAttribute('aria-label', 'Disponível em São Leopoldo e Porto Alegre');
      nativeBadge.setAttribute('title', 'Disponível em São Leopoldo e Porto Alegre');
      nativeBadge.classList.add('location-badge--logical-merged');
      return;
    }

    const image = card.querySelector('.pk-variant-grid-image, .card-gallery, [ref="cardGallery"], .product-card__media');
    if (image && !image.querySelector('.pdl-logical-generated-badge')) {
      image.insertAdjacentHTML('beforeend', badgeMarkup());
    }
  }

  function sourcePayload(group) {
    return group.map((item) => ({
      productId: item.productId,
      variantId: item.variantId,
      handle: item.product.handle,
      sku: item.sku,
      skuSetKey: item.skuSetKey || '',
      location: item.location,
      inventory: item.inventory,
      available: item.available,
      variants: (item.product.variants || []).map((variant) => ({
        id: String(variant.id),
        sku: String(variant.sku || ''),
        title: variant.title,
        options: variant.options || [],
        price: safeNumber(variant.price),
        inventory: safeNumber(variant.inventory_quantity),
        available: Boolean(variant.available) || safeNumber(variant.inventory_quantity) > 0,
        locations: variant.locations || []
      }))
    }));
  }

  function updateVisibleCount(root, hiddenCount) {
    const candidates = [...root.querySelectorAll('*')].filter((element) => {
      if (element.children.length) return false;
      return /^\s*\d+\s+ITENS?\s*$/i.test(element.textContent || '');
    });

    candidates.forEach((element) => {
      if (!element.dataset.pdlOriginalCount) {
        const match = (element.textContent || '').match(/\d+/);
        if (match) element.dataset.pdlOriginalCount = match[0];
      }
      const original = safeNumber(element.dataset.pdlOriginalCount);
      if (!original) return;
      const excludedProjects = [...root.querySelectorAll('[data-pdl-project-excluded-count]')].reduce((sum, element) => sum + safeNumber(element.textContent), 0);
      const visible = Math.max(0, original - hiddenCount - excludedProjects);
      element.textContent = `${visible} ${visible === 1 ? 'ITEM' : 'ITENS'}`;
    });
  }

  function uniqueCards(cards) {
    return [...new Set(cards.filter((card) => card instanceof Element))];
  }

  async function reconcileCards(root, incomingCards = [], preferExisting = false) {
    if (!root) return;

    const existingCards = [...root.querySelectorAll('.product-grid__item')];
    const incoming = uniqueCards(Array.from(incomingCards || []));
    const cards = uniqueCards([...existingCards, ...incoming]);
    if (!cards.length) return;

    const previousCanonicalCards = new Set(
      cards.filter((card) => card.dataset.pdlLogicalCanonical === 'true')
    );
    const preferredExistingCards = new Set(
      preferExisting
        ? existingCards.filter((card) => card.dataset.pdlLogicalHidden !== 'true' && !card.hidden)
        : []
    );

    root.setAttribute('data-pdl-logical-loading', 'true');

    try {
      // Resolve all product data first while the visible grid remains untouched.
      const items = (await Promise.all(cards.map(readCard))).filter(Boolean);
      const groups = buildGroups(items);

      // From here to the end there is no await: reset + regroup happens in one
      // browser task, preventing the old "unhide then hide again" flash.
      restoreCards(cards);

      let hiddenCount = 0;

      groups.forEach((group) => {
        const uniqueProducts = new Set(group.map((item) => item.productId));
        if (group.length < 2 || uniqueProducts.size < 2) return;

        const canonical = chooseStableCanonical(group, previousCanonicalCards, preferredExistingCards);
        canonical.card.classList.add('pdl-logical-card--merged');
        canonical.card.dataset.pdlLogicalCanonical = 'true';
        canonical.card.dataset.pdlLogicalSources = JSON.stringify(sourcePayload(group));
        showMergedBadge(canonical.card);

        group.forEach((item) => {
          if (item === canonical) return;
          item.card.hidden = true;
          item.card.setAttribute('aria-hidden', 'true');
          item.card.dataset.pdlLogicalHidden = 'true';
          hiddenCount += 1;
        });
      });

      incoming.forEach((card) => {
        card.dataset.pdlLogicalPrepared = 'true';
      });

      updateVisibleCount(root, hiddenCount);
      root.dataset.pdlLogicalReady = 'true';
      document.dispatchEvent(new CustomEvent('pdl:logical-cards-ready', {
        detail: {
          hiddenCards: hiddenCount,
          visibleCards: cards.length - hiddenCount,
          preparedIncomingCards: incoming.length
        }
      }));
    } finally {
      root.removeAttribute('data-pdl-logical-loading');
    }
  }

  function enqueueWork(task) {
    const run = workQueue.then(task);
    workQueue = run.catch((error) => {
      console.warn('[Pedalokos] Falha ao estabilizar os cards lógicos:', error);
    });
    return run;
  }

  function applyLogicalCards() {
    const root = document.querySelector('results-list');
    if (!root || root.querySelector('[data-pdl-smart-mount]')) return Promise.resolve();
    return enqueueWork(() => reconcileCards(root));
  }

  function prepareIncomingCards(incomingCards, grid) {
    const incoming = uniqueCards(Array.from(incomingCards || []));
    if (!incoming.length) return Promise.resolve();

    const root = grid?.closest?.('results-list') || document.querySelector('results-list');
    if (!root || root.querySelector('[data-pdl-smart-mount]')) return Promise.resolve();

    return enqueueWork(() => reconcileCards(root, incoming, true));
  }

  function scheduleApply(delay = 120) {
    window.clearTimeout(scheduled);
    scheduled = window.setTimeout(() => {
      applyLogicalCards().catch((error) => {
        console.warn('[Pedalokos] Não foi possível reagrupar os cards:', error);
      });
    }, delay);
  }

  window.PDLLogicalProductCards = {
    prepareIncomingCards,
    reconcile: applyLogicalCards
  };

  document.dispatchEvent(new CustomEvent('pdl:logical-cards-api-ready'));

  const start = () => {
    scheduleApply(0);
    const root = document.querySelector('results-list');
    if (!root) return;

    const observer = new MutationObserver((records) => {
      const addedCards = [];

      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;

          if (node.matches?.('.product-grid__item')) addedCards.push(node);
          node.querySelectorAll?.('.product-grid__item').forEach((card) => addedCards.push(card));
        });
      });

      if (!addedCards.length) return;

      const hasUnpreparedCards = addedCards.some(
        (card) => card.dataset.pdlLogicalPrepared !== 'true'
      );

      if (hasUnpreparedCards) scheduleApply();
    });

    observer.observe(root, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', () => scheduleApply());
  window.addEventListener('pageshow', () => scheduleApply());
})();

